import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import ArrearsBanner from '@/components/ArrearsBanner';
import BalancePill from '@/components/BalancePill';
import { API_BASE_URL } from '@/config/env';
import { redirectToLogin } from '@/utils/authRedirect';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

/** 仅本地开发显示主题设置抽屉，正式构建（含 CI 生产包）不展示 */
const isDev = process.env.NODE_ENV === 'development';

/** 未登录也可访问的 /user 子路径 */
const userPublicPaths = new Set([
  '/user/login',
  '/user/register',
  '/user/register-result',
  '/user/notices',
]);

const getStoredCurrentUser = (): API.CurrentUser | undefined => {
  try {
    const stored = localStorage.getItem('currentUser');
    if (!stored) return undefined;
    return JSON.parse(stored) as API.CurrentUser;
  } catch (_error) {
    return undefined;
  }
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    return getStoredCurrentUser();
  };
  /** 登录/注册页也要带上 currentUser，避免跳到业务页时被 onPageChange 误踢回登录 */
  const currentUser = getStoredCurrentUser();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [<BalancePill key="balance" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: false,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login（静默兑换页需先完成兑换再写入 currentUser）
      if (
        !initialState?.currentUser &&
        !userPublicPaths.has(location.pathname)
      ) {
        redirectToLogin(location.pathname + location.search);
      }
    },
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <ArrearsBanner />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: API_BASE_URL,
  ...errorConfig,
};
